import React, { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { TeamName } from './ScheduleWeeksView';
import { getGameTypeLabel } from './scheduleFormModel';
import { formatLocalDate, formatLocalTime } from '../../utils/scheduleFormat';

const KNOCKOUT_TYPES = new Set(['Playoff', 'SemiFinal', 'Final']);

const describeVenue = (game) => [game.venueName, game.fieldName].filter(Boolean).join(' / ');

const TypeChip = ({ gameType }) => (
  <Chip
    label={gameType === 'Bye' ? 'BYE' : getGameTypeLabel(gameType)}
    size="small"
    color={KNOCKOUT_TYPES.has(gameType) ? 'primary' : 'default'}
    variant={KNOCKOUT_TYPES.has(gameType) ? 'outlined' : 'filled'}
    clickable={false}
    onClick={() => {}}
  />
);

const Side = ({ teamId, teamName, label, teamsById }) => (teamId
  ? <TeamName teamId={teamId} name={teamName} teamsById={teamsById} />
  : <>{label || 'TBD'}</>);

const groupByDate = (games) => {
  const groups = [];
  games.forEach((game) => {
    const last = groups[groups.length - 1];
    if (last && last.date === game.date) last.games.push(game);
    else groups.push({ date: game.date, games: [game] });
  });
  return groups;
};

const ScheduleExtraGamesView = ({ games = [], teams = [] }) => {
  const teamsById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const groups = useMemo(() => groupByDate(games), [games]);

  if (games.length === 0) {
    return <Typography color="text.secondary">No playoff or other games.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {groups.map((group) => (
        <Paper key={group.date} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }} data-testid="extra-games-date">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {formatLocalDate(group.date)}
          </Typography>
          <Stack spacing={0.5}>
            {group.games.map((game, i) => {
              const isBye = game.gameType === 'Bye';
              return (
                <Box
                  key={game.index ?? `${game.startTime}-${i}`}
                  data-testid="extra-game"
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 0.5, sm: 2 },
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: { sm: 80 }, fontWeight: 600 }}>
                    {formatLocalTime(game.startTime)}
                  </Typography>
                  <Box sx={{ minWidth: { sm: 110 } }}>
                    <TypeChip gameType={game.gameType} />
                  </Box>
                  {isBye ? (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      <TeamName teamId={game.homeTeamId} name={game.homeTeamName} teamsById={teamsById} />
                      {' BYE'}
                    </Typography>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          <Side teamId={game.homeTeamId} teamName={game.homeTeamName} label={game.homeLabel} teamsById={teamsById} />
                          {' vs '}
                          <Side teamId={game.awayTeamId} teamName={game.awayTeamName} label={game.awayLabel} teamsById={teamsById} />
                        </Typography>
                        {game.isTbd && (
                          <Chip label="TBD" size="small" color="warning" variant="outlined" clickable={false} onClick={() => {}} />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', ml: { sm: 'auto' } }}>
                        {describeVenue(game)}
                      </Typography>
                    </>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default ScheduleExtraGamesView;
